import { AiResponse } from '../ToggleSortByTag/components/SmartPaste/Api';
import AiApi from '../ToggleSortByTag/components/SmartPaste/Api';
import { KjedeTreNode } from 'components/KjedePicker';
import KjedeApi from 'services/KjederegisteretAdminApi/KjedeApi';
import { FormData, Kjede } from './types';
import {
  createBasicInfoPrompt,
  createMarkedskjedePrompt,
  createRegionalkjedePrompt,
} from './aiPrompts';
import {
  tryParseStringAsDate,
  tryParseAsNumber,
  parseCurrencyToNumber,
  validatePhoneNumber,
} from './utils';

const getAIResponse = async (prompt: string) => {
  try {
    const response = await AiApi.ask(prompt);
    return response;
  } catch (error) {
    console.error('Error fetching AI response: ', error);
    throw new Error('Failed to get AI response');
  }
};

const findKjedeTreNodeByKjedenr = (
  kjeder: KjedeTreNode[],
  kjedenr: number,
): KjedeTreNode | null => {
  for (const kjede of kjeder) {
    if (kjede.kjedenr === kjedenr) {
      return kjede;
    }
    if (kjede.children) {
      const found = findKjedeTreNodeByKjedenr(kjede.children, kjedenr);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

export interface SmartPasteResult {
  formData: FormData;
  markedskjedeResponse: AiResponse | null;
  regionalkjedeResponse: AiResponse | null;
}

export const handleSmartPaste = async (
  mappedMarkedKjeder: Kjede[],
  mappedRegionKjeder: Kjede[],
  markedKjeder: KjedeTreNode[],
): Promise<SmartPasteResult> => {
  const text = await navigator.clipboard.readText();

  console.log('=== Smart Paste Started ===');
  console.log('Input Text:', text);

  // Step 1: Make three separate AI calls in parallel
  console.log('Making 3 separate AI prompt calls...');
  const [basicInfoResponseFromAI, markedskjedeResponseFromAI, regionalkjedeResponseFromAI] =
    await Promise.all([
      getAIResponse(createBasicInfoPrompt(text)),
      getAIResponse(createMarkedskjedePrompt(text, mappedMarkedKjeder)),
      getAIResponse(createRegionalkjedePrompt(text, mappedRegionKjeder)),
    ]);

  console.log('Basic Info Response:', basicInfoResponseFromAI);
  console.log('Markedskjede Response:', markedskjedeResponseFromAI);
  console.log('Regionalkjede Response:', regionalkjedeResponseFromAI);

  // Default form data
  let formData: FormData = {
    gln: '',
    organisasjonsnummer: '',
    telefonnummer: '',
    besoksadresse: '',
    nedlagtdato: null,
    enhetnavn: '',
    butikknummer: '',
    firmanavn: '',
    besokpostnummer: '',
    besokpoststed: '',
    fakturaadresse: '',
    fakturapostnummer: '',
    fakturapoststed: '',
    postadresse: '',
    postnummer: '',
    postpoststed: '',
    kommentar: '',
    leverandorkundenummer: '',
    leverandorkundenummerhos: '',
    kundenummerhossgrossist: '',
    bransjeforslag: '',
    kontonummer: '',
    salgsareal: 0,
    fakturaEpost: '',
    epost: '',
    oppstartsdato: null,
    omsetning: null,
    markedskjede: '',
    markedskjedeNr: '',
    regionalkjede: '',
    regionalkjedeNr: '',
    medlemHos: '',
    medlemsNummer: '',
    medlemFraOgMedDato: null,
    medlemTilOgMedDato: null,
  };

  // Step 3: Process the responses
  if (typeof basicInfoResponseFromAI === 'object') {
    const regionalKjedeToUseInFormdata = {
      kjedenavn: '',
      kjedenr: '',
    };
    const markedsKjedeToUseInFormdata = {
      kjedenavn: '',
      kjedenr: '',
    };

    const parsedKjedeNr = markedskjedeResponseFromAI?.markedskjedeNr
      ? parseInt(markedskjedeResponseFromAI.markedskjedeNr)
      : NaN;
    const foundMarkedskjede = !isNaN(parsedKjedeNr)
      ? findKjedeTreNodeByKjedenr(markedKjeder, parsedKjedeNr)
      : null;

    if (foundMarkedskjede) {
      if (foundMarkedskjede.children && foundMarkedskjede.children.length > 0) {
        // call leaf endoint to find correct child node
        try {
          const parsedSalgsAreal = parseInt(basicInfoResponseFromAI.salgsareal);
          if (!isNaN(parsedSalgsAreal)) {
            const markedKjedeResponse = await KjedeApi.markedskjedeLeafBySalgsareal(
              foundMarkedskjede.kjedenr.toString(),
              parsedSalgsAreal,
            );
            console.log('Marked Kjede from API:', markedKjedeResponse.data);
            markedsKjedeToUseInFormdata.kjedenr = markedKjedeResponse.data.kjedenr.toString();
            markedsKjedeToUseInFormdata.kjedenavn = markedKjedeResponse.data.kjedenavn;
          }
        } catch (error) {
          console.warn('Could not find markedskjede leaf with API, using parent kjede:', error);
        }
      } else {
        // found kjede is a leaf node, so we can use it directly
        console.log('Found markedskjede is a leaf node, using it directly:', foundMarkedskjede);
        markedsKjedeToUseInFormdata.kjedenr = foundMarkedskjede.kjedenr.toString();
        markedsKjedeToUseInFormdata.kjedenavn = foundMarkedskjede.name;
      }
    }

    console.log(basicInfoResponseFromAI.omsetning);

    // Update form data with all fields
    formData = {
      gln: basicInfoResponseFromAI.gln || '',
      organisasjonsnummer: basicInfoResponseFromAI.organisasjonsnummer || '',
      telefonnummer: validatePhoneNumber(basicInfoResponseFromAI.telefonnummer || ''),
      besoksadresse: basicInfoResponseFromAI.besoksadresse || '',
      nedlagtdato: tryParseStringAsDate(basicInfoResponseFromAI.nedlagtdato) ?? null,
      enhetnavn: basicInfoResponseFromAI.enhetnavn || '',
      butikknummer: basicInfoResponseFromAI.butikknummer || '',
      firmanavn: basicInfoResponseFromAI.firmanavn || '',
      besokpostnummer: basicInfoResponseFromAI.besokpostnummer || '',
      besokpoststed: basicInfoResponseFromAI.besokpoststed || '',
      fakturaadresse: basicInfoResponseFromAI.fakturaadresse || '',
      fakturapostnummer: basicInfoResponseFromAI.fakturapostnummer || '',
      fakturapoststed: basicInfoResponseFromAI.fakturapoststed || '',
      postadresse: basicInfoResponseFromAI.postadresse || '',
      postnummer: basicInfoResponseFromAI.postnummer || '',
      postpoststed: basicInfoResponseFromAI.postpoststed || '',
      kommentar: basicInfoResponseFromAI.kommentar || '',
      leverandorkundenummer: basicInfoResponseFromAI.leverandorkundenummer || '',
      leverandorkundenummerhos: basicInfoResponseFromAI.leverandorkundenummerhos || '',
      kundenummerhossgrossist: basicInfoResponseFromAI.kundenummerhossgrossist || '',
      bransjeforslag: basicInfoResponseFromAI.bransjeforslag || '',
      kontonummer: basicInfoResponseFromAI.kontonummer || '',
      oppstartsdato: tryParseStringAsDate(basicInfoResponseFromAI.oppstartsdato) ?? null,
      omsetning: parseCurrencyToNumber(basicInfoResponseFromAI.omsetning),
      salgsareal: tryParseAsNumber(basicInfoResponseFromAI.salgsareal),
      fakturaEpost: basicInfoResponseFromAI.fakturaEpost || '',
      epost: basicInfoResponseFromAI.butikkEpost || '',

      // disse skal kun settes hvis kjeder er leaf kjeder
      markedskjede: markedsKjedeToUseInFormdata.kjedenavn || '',
      markedskjedeNr: markedsKjedeToUseInFormdata.kjedenr || '',
      regionalkjede: regionalKjedeToUseInFormdata.kjedenavn || '',
      regionalkjedeNr: regionalKjedeToUseInFormdata.kjedenr || '',

      // nye fields
      medlemHos: basicInfoResponseFromAI.MedlemHos || '',
      medlemsNummer: (basicInfoResponseFromAI as any).MedlemsNummer || '',
      medlemFraOgMedDato:
        tryParseStringAsDate((basicInfoResponseFromAI as any).MedlemFraOgMedDato) ?? null,
      medlemTilOgMedDato:
        tryParseStringAsDate((basicInfoResponseFromAI as any).MedlemTilOgMedDato) ?? null,
    };
  }

  console.log('=== Smart Paste Finished ===');

  return {
    formData,
    markedskjedeResponse: markedskjedeResponseFromAI,
    regionalkjedeResponse: regionalkjedeResponseFromAI,
  };
};

export default handleSmartPaste;
